import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export interface AIAnalysisResult {
  suggestions: string[];
  improvements: {
    category: string;
    details: string;
    example?: string;
  }[];
  spamScore: number;
  readabilityScore: number;
  tone: string;
  keywords: string[];
}

export const analyzeEmailContent = async (html: string): Promise<AIAnalysisResult> => {
  try {
    // Extract text content from HTML
    const textContent = html.replace(/<[^>]*>/g, ' ')
                          .replace(/\s+/g, ' ')
                          .trim();

    const response = await openai.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: `You are an expert email marketing analyst with deep knowledge of email best practices, deliverability, and engagement optimization.

Key Analysis Areas:
1. Subject Line & Preview Text
- Analyze for clarity, urgency, and value proposition
- Check for spam trigger words
- Evaluate length and impact

2. Content Structure
- Assess hierarchy and scanability
- Evaluate paragraph length and readability
- Check call-to-action placement and clarity

3. Engagement Factors
- Analyze personalization opportunities
- Evaluate emotional appeal and relevance
- Check for social proof elements

4. Technical Optimization
- Identify potential deliverability issues
- Check for mobile responsiveness indicators
- Evaluate content-to-image ratio

Format your response exactly like this:
Suggestions:
1. [First suggestion]
2. [Second suggestion]
...

Spam Score: [0-100]
Readability Score: [0-100]
Tone: [Professional/Casual/Promotional/etc.]
Keywords: [key1], [key2], [key3]

Keep suggestions actionable and specific. Focus on measurable improvements.`
        },
        {
          role: "user",
          content: `Analyze this email content and provide detailed feedback following the specified format:

${textContent}`
        }
      ],
      temperature: 0.7,
      max_tokens: 1000
    });

    const analysis = response.choices[0].message.content;
    if (!analysis) {
      throw new Error('No analysis content received from OpenAI');
    }
    
    // Parse the AI response into structured data
    const suggestionMatch = analysis.match(/suggestions?:.*?(?=spam|$)/is);
    const suggestions = suggestionMatch?.[0]
      ?.split('\n')
      .filter((line: string) => line.trim().length > 0)
      .map((line: string) => line.replace(/^\d+\.\s*/, '').trim()) || [];

    const spamScore = parseInt(analysis.match(/spam[^:]*:\s*(\d+)/i)?.[1] || "50");
    const readabilityScore = parseInt(analysis.match(/readability[^:]*:\s*(\d+)/i)?.[1] || "70");
    const tone = analysis.match(/tone[^:]*:\s*([^\n]+)/i)?.[1]?.trim() || "Neutral";
    const keywords = analysis.match(/keywords[^:]*:([^\n]+)/i)?.[1]?.split(',').map((k: string) => k.trim()) || [];

    const improvements = [
      {
        category: "Content Structure",
        details: "Analysis of content organization and readability",
        example: suggestions[0]
      },
      {
        category: "Engagement",
        details: "Recommendations for improving reader engagement",
        example: suggestions[1]
      },
      {
        category: "Technical",
        details: "Technical optimization suggestions",
        example: suggestions[2]
      }
    ];

    return {
      suggestions,
      improvements,
      spamScore,
      readabilityScore,
      tone,
      keywords
    };
  } catch (error) {
    console.error('Error analyzing email content:', error);
    throw error;
  }
}; 